import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { AlertType } from '@utils/constants';

import Alert from '../components/alert';

const AlertsContainer = styled.div`
  max-width: 300px;
  position: fixed;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1100;
`;

const MAX_ALERTS = 5;

type AlertProps = {
  id: string;
  type: AlertType;
  message: string;
};

type AlertContextType = {
  showAlert: (type: AlertType, message: string, duration?: number) => void;
};

type AlertContextProvider = {
  children: ReactNode;
};

export const AlertContext = createContext<AlertContextType>({
  showAlert: () => {},
});

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within a AlertProvider');
  }
  return context;
};

export const AlertProvider: FC<AlertContextProvider> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertProps[]>([]);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach(clearTimeout);
      timeoutsRef.current = {};
    };
  }, []);

  useEffect(() => {
    let container = document.getElementById('alert-portal-container');

    if (!container) {
      container = document.createElement('div');
      container.id = 'alert-portal-container';
      document.body.appendChild(container);
    }

    setPortalContainer(container);

    return () => {
      Object.values(timeoutsRef.current).forEach(clearTimeout);

      if (
        container &&
        container.parentElement &&
        !container.childElementCount
      ) {
        container.parentElement.removeChild(container);
      }
    };
  }, []);

  const hideAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));

    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
  }, []);

  const showAlert = useCallback(
    (type: AlertType, message: string, duration = 3000) => {
      const id = `alert-${Date.now().toString()}`;
      const alertMessage: AlertProps = {
        id,
        type,
        message,
      };

      setAlerts((prev) => {
        const newAlerts = [...prev, alertMessage];
        return newAlerts.slice(-MAX_ALERTS);
      });

      if (duration > 0) {
        const timeoutId = setTimeout(() => {
          hideAlert(id);
        }, duration);

        timeoutsRef.current[id] = timeoutId;
      }

      return id;
    },
    [hideAlert],
  );

  const renderAlerts = () => {
    if (!portalContainer || alerts.length === 0) {
      return null;
    }

    return createPortal(
      <AlertsContainer role='log' aria-live='polite'>
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            type={alert.type}
            message={alert.message}
            onClick={() => hideAlert(alert.id)}
          />
        ))}
      </AlertsContainer>,
      portalContainer,
    );
  };

  return (
    <AlertContext.Provider
      value={{
        showAlert,
      }}
    >
      {children}

      {renderAlerts()}
    </AlertContext.Provider>
  );
};
